import { useMemo, useRef } from "react";

import { useDispatch } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { multiIncludeExcludeType } from "app/components/multi-include-exclude-filter/consts";
import { MultiIncludeExcludeFilterContainer } from "app/components/multi-include-exclude-filter/MultiIncludeExcludeFilterContainer";
import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import { BrandedNonBrandedFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/BrandedNonBrandedFilter";
import type { IChangeFilterItems } from "app/components/filters-bar/change-filter/ChangeFilter";
import { EChangeFilterValues } from "app/components/filters-bar/change-filter/ChangeFilter";
import { ChangeFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/ChangeFilterForWebsiteKeywords";
import { CpcFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/CpcFilter";
import { SearchEnginesFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/SearchEnginesFilter";
import { SearchTypesFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/SearchTypesFilter";
import { VolumeFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/VolumeFilter";
import type { IWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import { WebsiteKeywordsPageTableTopContextProvider } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import { getWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageUtillities";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { getCorrectedParamsForPresetSelection } from "../presets/WebsiteKeywordsPage2CompetitiveFilterUtils";
import { useWebsiteKeywordsPage2Context } from "../WebsiteKeywordsPage2Context";

const changeFilterItems: IChangeFilterItems[] = [
    {
        get text() {
            return translate("change.filter.trending");
        },
        id: EChangeFilterValues.Trending,
        get tooltip() {
            return translate("change.filter.trending.tooltip");
        },
    },
    {
        get text() {
            return translate("change.filter.de-trending");
        },
        id: EChangeFilterValues.DeTrending,
        get tooltip() {
            return translate("change.filter.detrending.tooltip");
        },
    },
    {
        get text() {
            return translate("change.filter.lost-clicks");
        },
        id: EChangeFilterValues.LostClicks,
        get tooltip() {
            return translate("change.filter.lost.tooltip");
        },
    },
    {
        get text() {
            return translate("change.filter.new-clicks");
        },
        id: EChangeFilterValues.NewClicks,
        get tooltip() {
            return translate("change.filter.new.tooltip");
        },
    },
];

export const WebsiteKeywordsPage2Filters = () => {
    const dispatch = useDispatch();
    const { isCompare, isLoading: isLoadingData, searchEngineList, apiParams } = useWebsiteKeywordsPage2Context();

    const params = useSearchAdvertisingDataAndParams();
    const { webSource, volumeFromValue, volumeToValue, cpcFromValue, cpcToValue, Change, family, source } = params;
    const sourceType = apiParams.sourceType;

    const initialFiltersStateObject = useRef(apiParams);

    const contextValue = useMemo<IWebsiteKeywordsPageTableTopContext>(() => {
        const getWebsiteKeywordsPageTableTopContextArgs = {
            isLoadingData,
            initialFiltersStateObject,
            isCompare,
            chosenItems: chosenSites.sitelistForLegend(),
            nextTableParams: {
                IncludeBranded: apiParams.IncludeBranded,
                IncludeNoneBranded: apiParams.IncludeNoneBranded,
                volumeFromValue,
                volumeToValue,
                cpcFromValue,
                cpcToValue,
                Change,
                family,
                source,
                sourceType,
            },
            addTempParams: (values) => {
                const newParams = getCorrectedParamsForPresetSelection(params, values);
                proNavigator.updateParams(newParams);
            },
            dispatch,
        };
        return getWebsiteKeywordsPageTableTopContext(getWebsiteKeywordsPageTableTopContextArgs);
    }, [isLoadingData, apiParams.IncludeBranded, apiParams.IncludeNoneBranded, isCompare]);

    return (
        <WebsiteKeywordsPageTableTopContextProvider value={contextValue}>
            <Flex
                background={colorsPalettes.bluegrey[100]}
                padding="0 0 24px"
                flexWrap="wrap"
                gap="8px"
            >
                <MultiIncludeExcludeFilterContainer filterType={multiIncludeExcludeType.KEYWORD} />
                <MultiIncludeExcludeFilterContainer filterType={multiIncludeExcludeType.URL} />
                <BrandedNonBrandedFilterForWebsiteKeywords />
                <ChangeFilterForWebsiteKeywords changeFilterItems={changeFilterItems} />
                <VolumeFilterForWebsiteKeywords />
                <CpcFilterForWebsiteKeywords />
                {webSource === devicesTypes.DESKTOP ? (
                    <SearchEnginesFilterForWebsiteKeywords
                        searchEngineList={searchEngineList}
                        withCount={false}
                    />
                ) : null}
                <SearchTypesFilterForWebsiteKeywords withCount={false} />
            </Flex>
        </WebsiteKeywordsPageTableTopContextProvider>
    );
};
